<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { pythonInteractiveCards } from "~/features/python/data/chapter2-interactive";

const noteBlocks = [
  {
    title: "Variables aur data storage",
    body:
      "Python me variable aik named storage hota hai jahan value temporarily rakhi jati hai. Yeh chapter ka base concept hai kyun ke calculations, comparisons aur logic sab isi stored data par kaam karte hain.",
  },
  {
    title: "Input aur output",
    body:
      "input() user se value leta hai aur print() result dikhata hai. Dono mil kar program ko interactive banate hain aur basic practical programs me sab se zyada use hote hain.",
  },
  {
    title: "Operators aur conditions",
    body:
      "Operators calculations aur comparisons ke liye use hote hain. If-else conditions program ko decision lene me help karti hain, is liye yeh board paper me bohat important concept hai.",
  },
  {
    title: "Loops aur repetition",
    body:
      "For aur while loops repeated tasks ko automate karte hain. Jab same kaam ko multiple times chalana ho to loops code ko short aur clear banate hain.",
  },
  {
    title: "Functions ka faida",
    body:
      "Function reusable code block hota hai. Is se same logic dobara likhne ki zarurat kam hoti hai aur code ko organize karna easy ho jata hai.",
  },
];

const keyPoints = [
  "Variable naam aur value ka relation clear rakho.",
  "input() value leta hai, print() output dikhata hai.",
  "Operators calculations aur comparisons dono me use hote hain.",
  "If-else decision making ka base hai.",
  "For aur while loops repetition ko control karte hain.",
  "Functions code ko modular aur reusable banate hain.",
];

const conceptCards = [
  {
    title: "Variables",
    detail: "Variable me data store hota hai. Achi naming aur clear value assignment chapter 2 ka first step hai.",
    to: "/11th/computer/python/variables/",
  },
  {
    title: "Data Types",
    detail: "int, float, string aur boolean alag tarah ka data rakhte hain. Har data type ka use samajhna zaroori hai.",
    to: "/11th/computer/python/data-types/",
  },
  {
    title: "Operators",
    detail: "Arithmetic, comparison aur logical operators expressions aur conditions banane me use hote hain.",
    to: "/11th/computer/python/operators/",
  },
  {
    title: "Loops",
    detail: "For aur while loop repeat work ko handle karte hain. Board questions me yeh bohat repeat hota hai.",
    to: "/11th/computer/python/loops/",
  },
  {
    title: "Functions",
    detail: "Functions code ko chhote reusable blocks me divide karte hain. Parameters aur return value un ka core idea hai.",
    to: "/11th/computer/python/functions/",
  },
  {
    title: "Programs",
    detail: "Input-output, loops aur conditions mil kar simple programs banate hain. Yahin se practical grip banti hai.",
    to: "/11th/computer/python/programs/",
  },
];

const supportLinks = [
  { title: "Python MCQs", to: "/11th/computer/python/mcqs/", description: "Concept parhne ke baad objective practice ke liye." },
  { title: "Python Short Questions", to: "/11th/computer/python/short-questions/", description: "Important short-answer prep ke liye." },
  { title: "Python Long Questions", to: "/11th/computer/python/long-questions/", description: "Descriptive prep aur expected answers ke liye." },
  { title: "Printable Notes PDF", to: "/11th/computer/notes-pdf/python/", description: "Clean printable revision layout ke liye." },
];

const faqs = [
  {
    question: "11th class Python notes me sab se pehle kya parhna chahiye?",
    answer:
      "Sab se pehle variables, input-output aur operators samjhein. Us ke baad conditions, loops aur functions par move karein.",
  },
  {
    question: "Chapter 2 Python notes kya cover karte hain?",
    answer:
      "Yeh notes variables, data types, input-output, operators, conditions, loops aur functions ke core concepts ko cover karte hain.",
  },
  {
    question: "1st year computer Chapter 2 Python notes kahan milte hain?",
    answer:
      "Chapter 2 Python notes isi page par milte hain jahan variables, data types, loops aur functions ko simple Roman Urdu me organize kiya gaya hai.",
  },
  {
    question: "Chapter 2 Python me kaun se topics sab se important hain?",
    answer:
      "Variables, input-output, operators, loops aur functions sab se important topics hain kyun ke inhi se short questions, long questions aur programs bante hain.",
  },
  {
    question: "Python notes ke baad kya prepare karna chahiye?",
    answer:
      "Notes ke baad MCQs, short questions aur programs practice par jaana best hota hai taake concept recall aur application dono strong ho sakein.",
  },
  {
    question: "Kya Chapter 2 Python 11th class computer ka important chapter hai?",
    answer:
      "Haan, Chapter 2 Python sab se important chapter hai kyun ke is se long questions, short questions aur practical-style questions aate hain.",
  },
];

usePageSeo({
  title: "Chapter 2 Python Notes | 11th Class Computer",
  description:
    "Chapter 2 Python notes for 11th class computer with variables, data types, input-output, loops, functions and revision links in simple Roman Urdu.",
  keywords:
    "chapter 2 python notes, 11th class python notes, class 11 computer python notes, first year python chapter 2 notes",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Python", item: "/11th/computer/python/" },
    { name: "Notes", item: "/11th/computer/python/notes/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "Chapter 2 Python Notes",
    items: [
      { name: "Variables Notes", path: "/11th/computer/python/variables/" },
      { name: "Data Types Notes", path: "/11th/computer/python/data-types/" },
      { name: "Operators Notes", path: "/11th/computer/python/operators/" },
      { name: "Loops Notes", path: "/11th/computer/python/loops/" },
      { name: "Functions Notes", path: "/11th/computer/python/functions/" },
      { name: "Programs Practice", path: "/11th/computer/python/programs/" },
      { name: "Python MCQs", path: "/11th/computer/python/mcqs/" },
      { name: "Python Short Questions", path: "/11th/computer/python/short-questions/" },
      { name: "Python Long Questions", path: "/11th/computer/python/long-questions/" },
      { name: "Python Notes PDF", path: "/11th/computer/notes-pdf/python/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
        <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Chapter 2 Notes</p>
        <h1 class="mt-3 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">Chapter 2 Python Notes</h1>
        <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
          Yeh 11th class computer ke Chapter 2 Python notes hain jahan variables, input-output, operators, loops aur functions ko simple Roman Urdu me samjhaya gaya hai.
        </p>
        <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
          Agar aapko Chapter 2 samajh kar prepare karna hai, to yeh page notes se start karne ka best route hai. Is ke baad MCQs aur programs practice par ja sakte hain.
        </p>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Quick Notes</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Python chapter ka concept flow</h2>
        </div>
        <div class="mt-5 grid gap-4 md:grid-cols-2">
          <article
            v-for="block in noteBlocks"
            :key="block.title"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ block.title }}</h3>
            <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ block.body }}</p>
          </article>
        </div>
      </section>

      <section class="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <div class="rounded-[28px] border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900/40 dark:bg-emerald-950/20 md:p-6">
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">Important Points</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Yaad rakhne wali cheezain</h2>
          <div class="mt-5 space-y-2 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p v-for="point in keyPoints" :key="point">- {{ point }}</p>
          </div>
        </div>

        <div class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Exam Shortcut</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Agar jaldi prepare karna ho</h2>
          <div class="mt-5 space-y-3 text-sm leading-7 text-slate-700 dark:text-slate-300">
            <p>- Pehle variables, input-output aur operators samjho</p>
            <p>- Phir if-else aur loops strong karo</p>
            <p>- Aakhir me functions aur programs practice karo</p>
            <p class="pt-2 font-medium text-sky-700 dark:text-sky-300">Yeh sequence follow karne se Chapter 2 samajhna easy hota hai.</p>
          </div>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Topic-wise Notes</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter 2 ko topic ke hisaab se kholo</h2>
        </div>
        <p class="mt-3 text-sm leading-7 text-slate-600 dark:text-slate-300">
          Agar kisi specific topic me weakness ho to neeche se direct us topic ka page kholo aur concept ko examples ke sath revise karo.
        </p>
        <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <NuxtLink
            v-for="card in conceptCards"
            :key="card.to"
            :to="card.to"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ card.title }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ card.detail }}</p>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practice After Notes</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Notes ke baad yeh pages kholo</h2>
        </div>
        <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <NuxtLink
            v-for="item in supportLinks"
            :key="item.to"
            :to="item.to"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">More Topic Practice</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Concept samjho phir practice karo</h2>
        </div>
        <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <NuxtLink
            v-for="item in pythonInteractiveCards"
            :key="item.to"
            :to="item.to"
            class="rounded-[22px] border border-slate-200 bg-white p-5 transition hover:border-sky-300 hover:bg-sky-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-sky-700 dark:hover:bg-slate-900"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
          </NuxtLink>
        </div>
      </section>

      <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Chapter 2 Python Notes FAQs</h2>
        </div>
        <div class="mt-5 space-y-4">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>
